'use client';

import { cn } from '@magicschool/spellbook/lib/utils';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { generateDistributedPoints, type PlaneConfig, type Point } from './utils';

type BaseCelebrationProps = {
  /** Host of the particle explosion. */
  children: React.ReactNode;
  /** Optional target ref to use for positioning the explosion origin instead of the wrapper element. */
  targetRef?: React.RefObject<HTMLElement | null>;
  /** Length of delay to the start of the animation in milliseconds. Useful for triggering on page load. */
  initialDelay?: number;
};

type CelebrationPropsControlled = {
  /** When transitioning from false to true, trigger animation. */
  shouldAnimate: boolean;
  /** Callback triggered when animation has finished. */
  onEnd: (event: React.AnimationEvent<HTMLDivElement>) => void;
} & BaseCelebrationProps;

type CelebrationPropsUncontrolled = {
  shouldAnimate?: never;
  onEnd?: never;
} & BaseCelebrationProps;

export type CelebrationProps = CelebrationPropsControlled | CelebrationPropsUncontrolled;

type ParticleProps = {
  initialDelay: number;
  x: number;
  y: number;
};

const Particle: React.FC<ParticleProps> = ({ initialDelay, x, y }) => {
  const randomAngleRef = useRef<number>(Math.random());
  const depthRef = useRef<number>(Math.floor(Math.random() * 8) + 4);

  return (
    <svg
      width={depthRef.current}
      height={depthRef.current}
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('absolute', 'animate-celebration-particle', 'origin-center', 'text-primary-solid-background')}
      style={{
        left: `${x}px`,
        top: `${y}px`,
        rotate: `${randomAngleRef.current}turn`,
        filter: `opacity(${(depthRef.current - 1) * 10}%)`,
        animationDelay: `${initialDelay}ms`,
      }}
    >
      <path
        d="M1.89037 2.54125L4.93283 9.95225C5.74272 11.9268 5.56379 14.1765 4.45196 15.9983L0.276278 22.8354C-0.647271 24.3656 0.905888 26.187 2.5413 25.4917L9.95274 22.4492C11.9274 21.6393 14.1772 21.8182 15.9991 22.9299L22.8365 27.1053C24.3667 28.0287 26.1883 26.4756 25.4929 24.8403L22.4504 17.4293C21.6406 15.4547 21.8195 13.205 22.9313 11.3832L27.107 4.54614C28.0305 3.01595 26.4774 1.19452 24.842 1.88989L17.4305 4.93235C15.4558 5.74223 13.206 5.56336 11.3842 4.45162L4.5468 0.276289C3.01656 -0.647183 1.195 0.905934 1.89037 2.54125Z"
        fill="currentColor"
      />
    </svg>
  );
};

const CONFIG: Pick<PlaneConfig, 'numPoints' | 'minDistance'> = {
  numPoints: 22,
  minDistance: 18,
};

/**
 * Wrapper component that creates an animated shower of sparkle particles around its child element, used to celebrate user achievements or completed actions.
 *
 * ### When to use
 * - Celebrating task completion (e.g., finishing an onboarding checklist, completing a quiz)
 * - Rewarding user milestones (e.g., progress bar reaching 100%)
 * - Providing positive feedback on a successful action (e.g., submitting a testimonial)
 *
 * ### When NOT to use
 * - Frequent or repetitive actions: use subtle feedback like a toast or icon change instead
 * - Background or automated events the user didn't initiate: celebration should feel earned
 * - Inline status indicators: use `Badge` or `ProgressBar` instead
 *
 * ### API
 * - `shouldAnimate`: Controlled mode trigger. When transitioning from `false` to `true`, the animation plays. Omit for uncontrolled mode (animates once on mount).
 * - `onEnd`: Required with `shouldAnimate`. Callback fired when the animation completes — use it to reset `shouldAnimate` back to `false`.
 * - `targetRef`: Optional ref to an element used as the explosion origin instead of the wrapper `div`. Useful when Celebration wraps a compound component but the effect should originate from a specific child (e.g., a `ProgressBar.Indicator`).
 * - `initialDelay`: Delay in milliseconds before the animation starts. Defaults to `250`. Useful for coordinating with page load or other transitions.
 *
 * ### Visual states
 * - **Idle**: No particles rendered, wrapper `div` is invisible
 * - **Animating**: 22 sparkle particles explode outward from the host element center, rendered in a portal with `role="presentation"` and `pointer-events-none`
 * - **Completed**: Particles removed from DOM after the system animation ends
 *
 * ### Accessibility
 * - The particle container uses `role="presentation"` — purely decorative, invisible to screen readers
 * - Particles are rendered into `document.body` via `createPortal` with `pointer-events-none` so they never block interaction
 * - Animation respects `prefers-reduced-motion` via the `motion-safe:` Tailwind prefix on the system animation
 *
 * ### Controlled vs uncontrolled
 * - **Controlled**: Pass `shouldAnimate` and `onEnd`. Toggle `shouldAnimate` to `true` to trigger, reset in `onEnd`. Supports re-triggering.
 * - **Uncontrolled**: Omit both `shouldAnimate` and `onEnd`. Animates once automatically after mount and page load. Cannot be re-triggered.
 */
export const Celebration: React.FC<CelebrationProps> = ({ children, shouldAnimate, onEnd, targetRef, initialDelay = 250 }) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const system = useRef<Point[] | null>(null);
  const [placement, setPlacement] = useState<{ x: number; y: number; width: number; height: number }>({ x: 0, y: 0, width: 0, height: 0 });
  const [internalIsRunning, setInternalIsRunning] = useState<boolean>(true);
  const [finishedDelay, setFinishedDelay] = useState<boolean>(false);

  useEffect(() => {
    // Allow layout to settle before immediately animating on mount
    if (!finishedDelay) {
      Promise.all([
        document.fonts.ready,
        new Promise((resolve) => {
          if (document.readyState === 'complete') {
            resolve(null);
          } else {
            window.addEventListener('load', () => resolve(null), { once: true });
          }
        }),
      ]).then(() => {
        setFinishedDelay(true);
      });
    }
  }, [finishedDelay, setFinishedDelay]);

  useLayoutEffect(() => {
    // Use targetRef if provided, otherwise use hostRef
    const refToUse = targetRef?.current || hostRef.current;
    if (!(finishedDelay && (shouldAnimate || internalIsRunning) && refToUse)) {
      return;
    }
    const rafId = requestAnimationFrame(() => {
      const { x, y, width, height } = refToUse.getBoundingClientRect();
      const systemWidth = Math.max(width * 0.6, 100);
      const systemHeight = Math.max(height * 0.6, 100);
      setPlacement({ x: x + width / 2, y: y + height / 2, width: systemWidth, height: systemHeight });
      system.current = generateDistributedPoints({ ...CONFIG, width: systemWidth, height: systemHeight });
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [finishedDelay, shouldAnimate, internalIsRunning, setPlacement, targetRef, system]);

  const points = system.current;
  const showParticles = finishedDelay && points !== null && (shouldAnimate ?? internalIsRunning);

  return (
    <div data-spellbook-name="celebration" ref={hostRef}>
      {children}
      {showParticles &&
        createPortal(
          <div
            className={cn('motion-safe:animate-celebration-system fixed origin-center z-portal pointer-events-none opacity-0')}
            style={{
              top: placement.y,
              left: placement.x,
              width: `${placement.width}px`,
              height: `${placement.height}px`,
              animationDelay: `${initialDelay}ms`,
            }}
            onAnimationEnd={(event) => {
              // Ignore propagated events from children
              if (event.target === event.currentTarget) {
                onEnd ? onEnd(event) : setInternalIsRunning(false);
              }
            }}
            role="presentation"
          >
            {points.map((point) => (
              <Particle key={`particle-${point.x}-${point.y}`} x={point.x} y={point.y} initialDelay={initialDelay} />
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};
